import { ReportType } from 'shared/domain/report/types/model/common';
import {
  isIssueListReportType,
  isSingleIssueReportType,
} from 'shared/domain/report/types/model/reportTypeGuards';

/**
 * Branded type for localStorage keys.
 * Ensures type safety when working with localStorage.
 */
export type LocalStorageKey = string & { readonly __brand: 'LocalStorageKey' };

/**
 * Identifies a specific DataGrid instance (by screen / entity).
 */
export enum DataGridInstanceName {
  company = 'company',
  contract = 'contract',
  inspection = 'inspection',
  user = 'user',
  documentation = 'documentation',
  issue = 'issue',
  standaloneFilterPanel = 'standaloneFilterPanel',
}

/**
 * Centralized factory for all localStorage keys.
 * 
 * This class is the single source of truth for localStorage key creation.
 * All key creation logic is encapsulated here, making it easy to:
 * - Discover available keys via autocomplete
 * - Ensure consistent key naming conventions
 * - Maintain type safety with the branded LocalStorageKey type
 * 
 * @example
 * // Static keys
 * LocalStorage.get(LocalStorageKeyFactory.AUTH_TOKEN, null);
 * 
 * // Dynamic keys
 * const key = LocalStorageKeyFactory.createIssueFiltersKey(projectId);
 * LocalStorage.set(key, filters);
 */
export class LocalStorageKeyFactory {

  static readonly AUTH_TOKEN = 'auth0_token' as LocalStorageKey;
  static readonly AUTH_TOKEN_EXPIRATION = 'auth0_token_expiration' as LocalStorageKey;
  static readonly USER_EMAIL = 'user_email' as LocalStorageKey;
  static readonly USER_ID = 'user_id' as LocalStorageKey;
  static readonly WORKFLOW_STEPPER_EXPANDED = 'workflow_stepper_expanded' as LocalStorageKey;
  /** Text colours the user picked last in a rich text editor (PT-7250), newest first. */
  static readonly RICH_TEXT_RECENT_COLORS = 'rich_text_recent_colors' as LocalStorageKey;

  private static readonly DATA_GRID_VERSION = 'v20';
  private static readonly ISSUE_DATA_GRID_VERSION = 'v21';

  static createDataGridKey(instance: DataGridInstanceName): LocalStorageKey {
    const version =
      instance === DataGridInstanceName.issue
        ? this.ISSUE_DATA_GRID_VERSION
        : this.DATA_GRID_VERSION;

    return `${instance}_mui_data_grid_storage_key_${version}` as LocalStorageKey;
  }

  static createDataGridFilterPresetsKey(
    instance: DataGridInstanceName
  ): LocalStorageKey {
    return `${this.createDataGridKey(instance)}_filter_presets` as LocalStorageKey;
  }

  static createDataGridSelectedPresetKey(
    instance: DataGridInstanceName
  ): LocalStorageKey {
    return `${this.createDataGridKey(instance)}_selected_preset` as LocalStorageKey;
  }

  static createIssueFiltersKey(uniqueId: string): LocalStorageKey {
    return `${uniqueId}_issue_filters` as LocalStorageKey;
  }

  static createIssueParametersKey(projectId: string): LocalStorageKey {
    return `issue_get_parameters_v2_${projectId}` as LocalStorageKey;
  }

  static createProjectNavigationTargetKey(projectId: string): LocalStorageKey {
    return `${projectId}_project_navigation_tab` as LocalStorageKey;
  }

  static createSearchPhraseKey(projectId: string, entityLabel: string): LocalStorageKey {
    return `${projectId}_${entityLabel}_search_phrase` as LocalStorageKey;
  }

  static createViewedTemplateKey(projectId: string): LocalStorageKey {
    return `viewed_template_${projectId}` as LocalStorageKey;
  }

  private static readonly REPORT_TYPE_PREFIXES: Record<ReportType, string> = {
    [ReportType.singleIssue]: 'issue',
    [ReportType.issueList]: 'issues',
    [ReportType.inspection]: 'inspection',
  };

  private static reportStoragePrefix(reportType: ReportType): string {
    if (isIssueListReportType(reportType)) {
      return 'report';
    }
    return this.REPORT_TYPE_PREFIXES[reportType];
  }

  static createReportConfigKey(reportType: ReportType): LocalStorageKey {
    return `${this.reportStoragePrefix(reportType)}_report_sections_config` as LocalStorageKey;
  }

  static createReportImageContentKey(reportType: ReportType): LocalStorageKey {
    return `${this.reportStoragePrefix(reportType)}_report_image_content` as LocalStorageKey;
  }

  static createReportShowMessageKey(reportType: ReportType): LocalStorageKey {
    return `${this.REPORT_TYPE_PREFIXES[reportType]}_report_show_message` as LocalStorageKey;
  }

  static createReportFieldsContentKey(reportType: ReportType): LocalStorageKey {
    return `${this.REPORT_TYPE_PREFIXES[reportType]}_report_fields_content` as LocalStorageKey;
  }

  static createReportSelectedFieldsKey(
    reportType: ReportType,
    projectId: string,
    processId?: string
  ): LocalStorageKey {
    const prefix = this.REPORT_TYPE_PREFIXES[reportType];
    if (isSingleIssueReportType(reportType)) {
      return `${prefix}_report_selected_fields_${projectId}_${processId}` as LocalStorageKey;
    }
    return `${prefix}_report_selected_fields_${projectId}` as LocalStorageKey;
  }

  static createReportShowPhoneNumbersKey(reportType: ReportType): LocalStorageKey {
    return `${this.REPORT_TYPE_PREFIXES[reportType]}_report_show_phone_numbers` as LocalStorageKey;
  }

  static createReportShowUserEmailsKey(reportType: ReportType): LocalStorageKey {
    return `${this.REPORT_TYPE_PREFIXES[reportType]}_report_show_user_emails` as LocalStorageKey;
  }

  static createReportShowImagesNamesAndDescriptionsKey(reportType: ReportType): LocalStorageKey {
    return `${this.REPORT_TYPE_PREFIXES[reportType]}_report_show_images_names_and_descriptions` as LocalStorageKey;
  }

  static createReportAdditionalContentExpandedKey(
    reportType: ReportType,
    accordionId?: string
  ): LocalStorageKey {
    const base = `${this.reportStoragePrefix(reportType)}_report_additional_content_expanded`;
    if (!accordionId) {
      return base as LocalStorageKey;
    }
    return `${base}_${accordionId}` as LocalStorageKey;
  }

  static createInspectionReportContentConfigKey(version: number): LocalStorageKey {
    return `inspection_report_content_config_v${version}` as LocalStorageKey;
  }

  static readonly LAST_REPORT_LOCALE = 'last_report_locale' as LocalStorageKey;

  static createCacheDataKey(id: string): LocalStorageKey {
    return `data_${id}` as LocalStorageKey;
  }

  static createCacheIdKey(id: string): LocalStorageKey {
    return `cache_id_${id}` as LocalStorageKey;
  }

  static createCacheLastUpdateKey(id: string): LocalStorageKey {
    return `last_update_${id}` as LocalStorageKey;
  }

  static createAutocompleteKey(baseKey: string): LocalStorageKey {
    return `autocomplete-${baseKey}` as LocalStorageKey;
  }

  static createChartSettingKey(chartId: string, setting: string): LocalStorageKey {
    return `${chartId}_${setting}` as LocalStorageKey;
  }

  static createDocumentationIssuesVisibleKey(projectId: string): LocalStorageKey {
    return `documentation_issues_visible_${projectId}` as LocalStorageKey;
  }

  /** Values chosen in the filters of an analytics dashboard section (PT-6804). */
  static createAnalyticsSectionFiltersKey(projectId: string, section: string): LocalStorageKey {
    return `analytics_section_filters_${projectId}_${section}` as LocalStorageKey;
  }

  static createDocumentationProgressActiveKey(projectId: string): LocalStorageKey {
    return `documentation_progress_active_${projectId}` as LocalStorageKey;
  }

  static readonly TEMPLATE_SEARCH_KEY = 'template_search_key' as LocalStorageKey;
  static readonly SELECTION_HAPPENED_KEY = 'hadChosenProject' as LocalStorageKey;
  static readonly CLOSED_INSTALL_MOBILE_APP_SNACKBAR_DATE = 'closedInstallMobileAppSnackbarDate' as LocalStorageKey;
  static readonly OLD_SW_CLEANED_FLAG = 'old_cra_sw_cleaned' as LocalStorageKey;
  static readonly SW_CONFIG_PROJECT_ID = 'sw-config-projectId' as LocalStorageKey;

  static readonly PERSIST_UI_KEY = 'persist:ui' as LocalStorageKey;

  static createTableColumnWidthKey(tableKey: string): LocalStorageKey {
    return tableKey as LocalStorageKey;
  }

  static createGenericKey(key: string): LocalStorageKey {
    return key as LocalStorageKey;
  }

  /**
   * Creates a user-scoped version of a key.
   * Used to isolate data per user in shared browser environments.
   */
  static createUserScopedKey(userId: string | null | undefined, baseKey: LocalStorageKey): LocalStorageKey {
    if (!userId) return baseKey;
    const shortUserId = userId.slice(0, 8);
    return `user_${shortUserId}_${baseKey}` as LocalStorageKey;
  }
}

